import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ModalService } from '@app/core/services/modal.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator } from '@app/modal/state-modal/state-modal.component';
import { ChangesCuttingPlanRealParams, CuttingPlanRealGet, CuttingPlanService, GetCuttingPlanParams } from '@app/services/cutting-plan.service';
import { Subject, takeUntil, groupBy, mergeMap, debounceTime, switchMap, EMPTY, catchError, from } from 'rxjs';

@Component({
    selector: 'tt-cutting-plan-real',
    templateUrl: './cutting-plan-real.component.html',
    styleUrls: ['./cutting-plan-real.component.css'],
})
export class CuttingPlanRealComponent implements OnInit, OnDestroy {
    public params?: GetCuttingPlanParams;

    public get?: CuttingPlanRealGet;

    private destroy$ = new Subject<void>();

    private change$ = new Subject<ChangesCuttingPlanRealParams>();

    public grid?: GridOptions;

    public saving = false;

    public distributing = false;

    constructor(
        private cuttingPlan: CuttingPlanService,
        private modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.change$
            .pipe(
                takeUntil(this.destroy$),
                groupBy((e) => e.colname_changed),
                mergeMap((group$) =>
                    group$.pipe(
                        debounceTime(250),
                        switchMap((e) => from(this.cuttingPlan.changesCuttingPlanReal(e)).pipe(catchError(() => EMPTY))),
                    ),
                ),
            )
            .subscribe({ next: this.onChangeCuttingPlanResponse });
    }

    private onChangeCuttingPlanResponse = (response: Partial<CuttingPlanRealGet>) => {
        if (!!this.get) {
            for (let k in response) {
                let key: keyof CuttingPlanRealGet = k as keyof CuttingPlanRealGet;

                this.get[key] = response[key] as any;

                if (this.get.pohcompletionprinciple_keyno === '1' || this.get.pohcompletionprinciple_keyno === '2') {
                    this.distribute();
                }
            }
        }
    };

    public async distribute() {
        if (!this.get) return;

        try {
            this.distributing = true;

            const records = this.grid?.gridfunc?.getAllRows() ?? [];

            const response = await this.cuttingPlan.distributeCuttingPlanReal({ ...this.get, records: records.slice().map((row) => ({ stockbatch_keyno: row['stockbatch_keyno'], quantity_pick_now: row['quantity_pick_now'] })) });

            const lookup: { [key: string]: string | number } = {};

            for (let row of response) {
                lookup['' + row.stockbatch_keyno] = row.quantity_picked_now;
            }

            for (let row of records) {
                row['quantity_pick_now'] = lookup['' + row['stockbatch_keyno']];
            }

            this.grid?.gridfunc?.refresh();
        } catch (error) {
            console.error(error);
        } finally {
            this.distributing = false;
        }
    }

    public async save() {
        if (!this.get) return;

        try {
            const records = this.grid?.gridfunc?.getAllRows().map((row) => ({ stockbatch_keyno: row['stockbatch_keyno'], quantity_pick_now: row['quantity_pick_now'] })) ?? [];
            const { errorcode, errormessage } = await this.cuttingPlan.saveCuttingPlanReal({ ...this.get, records });

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.navigator?.back();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    public onChange(key: keyof CuttingPlanRealGet) {
        if (!this.get) return;

        this.change$.next({ ...this.get, colname_changed: key });
    }

    private readGrid() {
        if (!this.grid) {
            this.grid = new Grid()
                .setRememberId('w_cuttingplanreal.grid')
                .setLoadDataFunction({ method: 3999, parameters: () => this.get })
                .setKendoConfig({ height: null });
        } else {
            this.grid.gridfunc?.read();
        }
    }

    async loadGet() {
        if (!!this.params && typeof this.params === 'object' && 'cuttingplan_keyno' && !isNaN(+this.params.cuttingplan_keyno)) {
            this.get = await this.cuttingPlan.getCuttingPlanReal(this.params);
        }
    }

    private async init() {
        await this.loadGet();
        this.navigator?.setHeading?.(this.get?.webpage_heading ?? '');
        this.readGrid();
    }

    ngOnInit(): void {
        if (!!this.navigator) {
            this.navigator.setSize?.({ width: 'min(120rem, 98vw)' });
            this.params = this.navigator.params as any;
            this.init();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
